import {
  Badge,
  Button,
  Card,
  Col,
  Image,
  Row,
  Space,
  Tag,
  Tooltip,
} from 'antd';
import clsx from 'clsx';
import moment from 'moment-timezone';
import React, { useState } from 'react';
import { useMedia } from 'react-use';
import { AudioIcon } from '../../../../assets/svg';
import {
  BREAKPOINTS,
  DATE_TIME_WITH_AT,
  GA_EVENT,
  GA_LABEL,
  INS_STATUS_CLASSNAME,
  INS_STATUS_KEYS,
  INS_STATUS_LABEL,
} from '../../../../common/constants';
import { Event } from '../../../../common/trackEvents';
import AudioPlayerModal from '../../../../components/AudioPlayerModal';
import EllipsisText from '../../../../components/EllipsisText';
import AddResponseDrawer from './AddResponseDrawer';
import AddResponseModal from './AddResponseModal';

function ResponseDetailCard({
  responseDetail,
  instructionData,
  index,
  count,
  token,
  refetch,
}) {
  const [showAudioModal, setShowAudioModal] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [previewImageKey, setPreviewImageKey] = useState('');
  const [showResponseModal, setShowResponseModal] = useState(false);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);

  return (
    <div
      id={`response-detail-card-${index}`}
      className={clsx(
        'response-detail',
        responseDetail?.status === INS_STATUS_KEYS?.RESPONDED &&
          'd-flex flex-vertical justify-end align-end',
        count - 1 === index && !isDesktopViewport && 'mb-80',
      )}
    >
      <div
        className="name-and-time d-flex"
        id={`response-detail-header-${index}`}
      >
        <Tooltip
          id={`response-detail-author-tooltip-${index}`}
          title={
            responseDetail?.responderUser?.name ||
            responseDetail?.rejector?.name ||
            responseDetail?.creator?.name
          }
        >
          <div className="ellipsis-text" id={`response-detail-author-${index}`}>
            {responseDetail?.responderUser?.name ||
              responseDetail?.rejector?.name ||
              responseDetail?.creator?.name}
          </div>
        </Tooltip>
        <Badge
          status="default"
          className="ml-5 mr-5"
          color="#676C77"
          id={`response-detail-separator-${index}`}
        />
        <span id={`response-detail-date-${index}`}>
          {moment(responseDetail?.createdAt)
            .tz(instructionData?.project?.timeZone)
            .format(DATE_TIME_WITH_AT)}
        </span>
      </div>
      <Card
        id={`response-detail-card-content-${index}`}
        className={clsx(
          'response-detail-card',
          responseDetail?.status === INS_STATUS_KEYS?.RESPONDED &&
            'grey-background',
        )}
      >
        <div
          className="instruction-tab-details"
          id={`response-detail-content-${index}`}
        >
          <Tag
            id={`response-detail-status-tag-${index}`}
            className={`${
              INS_STATUS_CLASSNAME[responseDetail?.status]
            } status-tag mb-12`}
          >
            {INS_STATUS_LABEL[responseDetail?.status]}
          </Tag>
          <div id={`response-detail-remark-${index}`}>
            <EllipsisText
              id={`response-detail-remark-text-${index}`}
              text={responseDetail?.remark || '-'}
            />
          </div>
          <Row gutter={18} align="middle" justify="space-between">
            {responseDetail?.photos?.length > 0 && (
              <Col>
                <Space orientation="vertical" size={12} className="mt-12">
                  <div id={`response-detail-images-wrapper-${index}`}>
                    <Image.PreviewGroup
                      id={`response-detail-image-preview-group-${index}`}
                      preview={{
                        open:
                          responseDetail?.revision === previewImageKey &&
                          isVisible,
                        onOpenChange: (visible) => {
                          setIsVisible(visible);
                          if (visible) {
                            Event(GA_EVENT.OPENED_IMAGE_VIEWER, {
                              label: GA_LABEL.OPENED_IMAGE_VIEWER,
                              // eslint-disable-next-line no-undef
                              pathname: window?.location?.href,
                            });
                          }
                        },
                      }}
                    >
                      <Space className="image-preview-wrapper issue-images">
                        {React.Children.map(
                          responseDetail?.photos,
                          (photo, photoIndex) => (
                            <Image
                              id={`response-detail-image-${index}-${photoIndex}`}
                              src={photo}
                              alt="logo"
                              height="64px"
                              width="64px"
                              onClick={() =>
                                setPreviewImageKey(responseDetail?.revision)
                              }
                            />
                          ),
                        )}
                        {responseDetail?.photos?.length > 1 && (
                          <div
                            id={`response-detail-images-extra-count-${index}`}
                            className="extra-count pointer"
                            onClick={() => {
                              setIsVisible(true);
                              setPreviewImageKey(responseDetail?.revision);
                            }}
                          >
                            {`+${responseDetail?.photos?.length - 1}`}
                          </div>
                        )}
                      </Space>
                    </Image.PreviewGroup>
                  </div>
                </Space>
              </Col>
            )}
            {responseDetail?.audios?.length > 0 && (
              <Col>
                <Space orientation="vertical" size={12}>
                  <div id={`response-detail-audio-wrapper-${index}`}>
                    <div
                      id={`response-detail-audio-player-${index}`}
                      className="audio-player-wrapper issue-audio"
                      onClick={() => {
                        setShowAudioModal(true);
                        Event(GA_EVENT.DOWNLOAD_AUDIO, {
                          label: GA_LABEL.DOWNLOAD_AUDIO,
                          // eslint-disable-next-line no-undef
                          pathname: window?.location?.href,
                        });
                      }}
                    >
                      <AudioIcon id={`response-detail-audio-icon-${index}`} />
                      {responseDetail?.audios?.length > 1 && (
                        <div
                          className="extra-count"
                          id={`response-detail-audio-extra-count-${index}`}
                        >{`+${responseDetail?.audios?.length - 1}`}</div>
                      )}
                    </div>
                  </div>
                </Space>
              </Col>
            )}
          </Row>
          {responseDetail?.audios?.length > 0 && (
            <AudioPlayerModal
              id={`response-detail-audio-modal-${index}`}
              audios={responseDetail?.audios}
              showModal={showAudioModal}
              setShowModal={setShowAudioModal}
            />
          )}
        </div>
        {responseDetail?.status === INS_STATUS_KEYS?.REJECTED &&
          instructionData?.status === INS_STATUS_KEYS?.REJECTED &&
          count - 1 === index && (
            <Button
              id={`response-detail-add-response-button-${index}`}
              shape="round"
              type="primary"
              className="b-0 width-percent-100 justify-center mt-16"
              onClick={() => setShowResponseModal(true)}
            >
              Add Response
            </Button>
          )}
        {showResponseModal && isDesktopViewport ? (
          <AddResponseModal
            id={`response-detail-add-response-modal-${index}`}
            showModal={showResponseModal}
            setShowModal={setShowResponseModal}
            instructionData={instructionData}
            token={token}
            refetch={refetch}
          />
        ) : (
          <AddResponseDrawer
            id={`response-detail-add-response-drawer-${index}`}
            open={showResponseModal}
            instructionData={instructionData}
            onClose={() => setShowResponseModal(false)}
            token={token}
            refetch={refetch}
          />
        )}
      </Card>
    </div>
  );
}

export default ResponseDetailCard;
